import {
    BlankModal,
    HStack,
    ModalBody,
    ModalFooter,
    ModalHeader,
    VStack,
    Button,
    InlineBanner,
    Text,
} from '@cad-arc';
import { useMutation } from '@tanstack/react-query';
import { useApiClient } from '@m43/core';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { useBroadcast } from '../common/broadcast-channel/use-broadcast';
import { useCallback } from 'react';
import { AuthenticationResource } from '@mark43/rms-api';
import { errorOccurredWhile } from '../common/helpers/error-occurred-while';
import { useLogout } from '../common/hooks/use-logout';

export type SessionTimeoutModalComponentType = typeof SessionTimeoutModal;
export function SessionTimeoutModal() {
    const logout = useLogout();
    const modalControls = useFullscreenModalControls();
    const { postMessage } = useBroadcast('session-timeout-modal');
    const onCancel = useCallback(() => {
        modalControls.close();
        postMessage({
            type: 'CLOSE_SESSION_TIMEOUT_MODAL',
        });
    }, [modalControls, postMessage]);
    useBroadcast('session-timeout-modal', (msg) => {
        if (msg.type === 'CLOSE_SESSION_TIMEOUT_MODAL') {
            onCancel();
        }
    });
    const apiClient = useApiClient();
    const mutation = useMutation(
        () => {
            return apiClient.request<AuthenticationResource.ExtendSession>({
                method: 'GET',
                url: 'auth/session/extend/',
                service: 'RMS',
            });
        },
        {
            onSuccess() {
                postMessage({
                    type: 'CLOSE_SESSION_TIMEOUT_MODAL',
                });
                onCancel();
            },
        }
    );
    return (
        <BlankModal
            size="md"
            onClose={onCancel}
            isOpen={true}
            closeOnEsc={false}
            closeOnOverlayClick={false}
        >
            <ModalHeader>Session Expires Soon</ModalHeader>
            <ModalBody>
                <VStack>
                    {mutation.isError ? (
                        <InlineBanner
                            status="error"
                            description={errorOccurredWhile('extending session')}
                        />
                    ) : undefined}
                    <Text>
                        Your session is about to expire. If you would like to continue working,
                        please click the button below. If you do not respond within 5 minutes the
                        system will log you out.
                    </Text>
                </VStack>
            </ModalBody>
            <ModalFooter>
                <HStack>
                    <Button
                        variant="ghost"
                        isDisabled={mutation.isLoading}
                        onClick={() => {
                            logout();
                        }}
                    >
                        Log Off
                    </Button>
                    <Button
                        variant="solid"
                        isLoading={mutation.isLoading}
                        isDisabled={mutation.isLoading}
                        onClick={() => {
                            mutation.mutate();
                        }}
                    >
                        Continue Session
                    </Button>
                </HStack>
            </ModalFooter>
        </BlankModal>
    );
}
